import { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { Button, Heading, Stack, Text, useToast } from '@eluve/components';
import { FHIR_MIRROR_ENTITIES, type FhirMirrorEntity } from '@eluve/fhir-utils';

/**
 * Admin control for the eluve→FHIR mirror.
 *
 * The mirror is normally kept current by a DB trigger, so this page exists for
 * the cases the trigger can't cover: tenants whose rows predate the mirror, and
 * re-syncs after a mapping change. Enqueue-only — jobs carry the same key the
 * trigger uses, so pressing the button twice, or pressing it while edits are
 * landing, still leaves one job per row.
 */

type MirrorProject = {
  id: string;
  name: string;
  tenantId: string | null;
};

/** One button per mirrored entity, so a new entity shows up here for free. */
const ENTITY_LABELS: Record<FhirMirrorEntity, string> = {
  location: 'Locations',
};

const TenantRow: React.FC<{ project: MirrorProject }> = ({ project }) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const [pendingEntity, setPendingEntity] = useState<string | null>(null);

  const backfill = async (entity: FhirMirrorEntity, label: string) => {
    if (!project.tenantId) return;
    setPendingEntity(entity);
    try {
      const result = await apiClient.medplumAdmin.backfillMirror({
        params: { tenantId: project.tenantId },
        body: { entity },
      });

      if (result.status === 200) {
        toast.success(
          `Queued ${result.body.enqueued} ${label.toLowerCase()} for ${project.name}.`,
        );
      } else {
        toast.error("Couldn't queue sync.");
      }
    } catch {
      toast.error('Failed to reach API');
    } finally {
      setPendingEntity(null);
    }
  };

  return (
    <tr className="border-b last:border-b-0">
      <td className="py-2">{project.name}</td>
      <td className="py-2 font-mono text-xs">
        {project.tenantId ?? <span className="text-text-tertiary">-</span>}
      </td>
      <td className="py-2">
        <Stack direction="horizontal" gap={2} fullWidth={false} justify="end">
          {FHIR_MIRROR_ENTITIES.map((entity) => (
            <Button
              key={entity}
              variant="outline"
              size="sm"
              // A project with no tenant has nothing in Postgres to mirror.
              disabled={!project.tenantId || pendingEntity !== null}
              onClick={() => void backfill(entity, ENTITY_LABELS[entity])}
              data-testid={`backfill-${entity}`}
            >
              {pendingEntity === entity
                ? 'Queueing…'
                : `Sync ${ENTITY_LABELS[entity]}`}
            </Button>
          ))}
        </Stack>
      </td>
    </tr>
  );
};

export const MedplumMirrorPage: React.FC = () => {
  const apiClient = useApiClient();
  const [projects, setProjects] = useState<MirrorProject[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const result = await apiClient.medplumAdmin.diagnostics();
        setProjects(result.status === 200 ? result.body.projects : []);
      } catch {
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [apiClient]);

  if (loading) {
    return <Text size="md">Loading tenants…</Text>;
  }

  return (
    <div className="space-y-6">
      <Heading size="4xl" weight="bold">
        FHIR Mirror
      </Heading>

      <Text size="md" color="tertiary">
        Queues a re-sync of a tenant's eluve rows into their FHIR mirror.
        Ongoing changes sync automatically; use this for tenants whose data
        predates the mirror, or after a mapping change. Safe to re-run.
      </Text>

      <div className="rounded-lg border p-4">
        <Heading size="2xl" weight="semibold" className="mb-4">
          Tenants ({projects?.length ?? 0})
        </Heading>
        {!projects || projects.length === 0 ? (
          <Text size="md" color="tertiary">
            No Medplum projects found.
          </Text>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-text-tertiary">
                <th className="pb-2 font-medium">Project</th>
                <th className="pb-2 font-medium">Tenant ID</th>
                <th className="pb-2 text-right font-medium">Backfill</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <TenantRow key={project.id} project={project} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
